import type { DynamicViewConfigModel } from '@supply-mover-2-0/shared';
import { create } from 'zustand';

/**
 * Dynamic View Store
 *
 * Holds the current dynamic view configuration generated by the AI agent.
 * The config is a "recipe" — it describes what data to fetch and how to display it.
 * The DynamicViewPage reads this config, fetches data, and renders the view.
 *
 * Lifecycle:
 * 1. Agent calls render_view tool → backend emits 'render-view' event
 * 2. Frontend receives event → stores config here → navigates to /dynamic-view
 * 3. DynamicViewPage reads config → fetches data → renders
 * 4. User navigates away → config persists in memory (can return to view)
 * 5. Agent sends new render-view → config is replaced
 */
export interface DynamicViewStoreState {
	/** Current view configuration (null = no view active) */
	currentView: DynamicViewConfigModel | null;
	/** Loading state for data source fetching */
	isLoading: boolean;
	/** Error message if data fetching failed */
	error: string | null;
}

export interface DynamicViewStoreActions {
	/** Set the current view configuration (called when agent emits render-view) */
	setCurrentView: (config: DynamicViewConfigModel) => void;
	/** Clear the current view */
	clearView: () => void;
	/** Set loading state */
	setLoading: (loading: boolean) => void;
	/** Set error state */
	setError: (error: string | null) => void;
}

export const useDynamicViewStore = create<
	DynamicViewStoreState & DynamicViewStoreActions
>((set) => ({
	// State
	currentView: null,
	isLoading: false,
	error: null,

	// Actions
	setCurrentView: (config: DynamicViewConfigModel) => {
		set({ currentView: config, error: null });
	},

	clearView: () => {
		set({ currentView: null, isLoading: false, error: null });
	},

	setLoading: (loading: boolean) => {
		set({ isLoading: loading });
	},

	setError: (error: string | null) => {
		set({ error, isLoading: false });
	},
}));
